import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import history from 'shared_redux/history';
import * as OrganizationsSelectors from 'shared_redux/selectors/models/organizations';
import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';
import styles from '../athlete_invite/styles.module.scss';

/**
 * GroupInviteAuth component handles authentication for group invites.
 * 
 * This component:
 * - Requires authentication for group invites
 * - Redirects unauthenticated users to sign in
 * - Redirects authenticated users to the group invite athlete creation flow
 */
const GroupInviteAuth = ({ isLoggedIn, userEmail, account }) => {
    const [loading, setLoading] = useState(true);
    const [inviteInfo, setInviteInfo] = useState(null);
    const [error, setError] = useState(null);
    const { code: inviteCode } = useParams();

    useEffect(() => {
        const fetchInviteInfo = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/api/group-invite-links/${inviteCode}`);
                
                if (!response.ok) {
                    // Try to parse the error message for more details
                    const errorText = await response.text();
                    
                    // Check if the error contains information about invite usage limit
                    if (errorText.includes('maximum uses') || 
                        errorText.includes('usage limit') || 
                        errorText.includes('reached its maximum')) {
                        throw new Error('This invitation link has reached its maximum number of uses. Please contact your coach or administrator for a new invitation.');
                    } else if (errorText.includes('expired')) {
                        throw new Error('This invitation link has expired. Please contact your coach or administrator for a new invitation.');
                    } else {
                        throw new Error('Invalid invite link');
                    }
                }
                
                const data = await response.json();
                setInviteInfo(data);
            } catch (err) {
                console.error('Error fetching group invite info:', err);
                setError(err.message || 'This invite link is invalid or has expired.');
            } finally {
                setLoading(false);
            }
        };

        if (inviteCode) {
            fetchInviteInfo();
        }
    }, [inviteCode]);

    useEffect(() => {
        // If user is logged in and we have valid invite info,
        // redirect to the athlete creation component
        if (isLoggedIn && inviteInfo && !loading) {
            history.push(`/group-athlete-create/${inviteCode}`);
        }
    }, [isLoggedIn, inviteInfo, loading, inviteCode]);

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.spinner}></div>
                    <p>Loading invitation details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Invitation Error</h2>
                    <div className={styles.error}>{error}</div>
                    <button 
                        className={styles.button} 
                        onClick={() => history.push('/login')}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // If user is not logged in but invite is valid
    if (!isLoggedIn && inviteInfo) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    {inviteInfo.organization?.logo_url && (
                        <div className={styles.logoContainer}>
                            <img 
                                src={inviteInfo.organization.logo_url} 
                                alt="Organization Logo" 
                                className={styles.logo} 
                            />
                        </div>
                    )}
                    
                    <h2>Group Invitation</h2>
                    
                    <p>You've been invited to join {inviteInfo.organization?.name || 'an organization'} as an athlete.</p>
                    
                    <p>You need to log in or create an account to continue.</p>
                    
                    <div className={styles.buttonGroup} style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <button 
                            className={styles.button} 
                            onClick={() => history.push(`/login?redirect=${encodeURIComponent(`/group-invite/${inviteCode}`)}`)}
                        >
                            Sign In
                        </button>
                        
                        <button 
                            className={styles.button} 
                            onClick={() => history.push(`/beta?redirect=${encodeURIComponent(`/group-invite/${inviteCode}`)}`)}
                        >
                            Create Account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Default loading state as a fallback
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.spinner}></div>
                <p>Processing...</p>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    const account = AccountsSelectors.getAccount(state);
    return {
        isLoggedIn: OrganizationsSelectors.getIsLoggedIn(state),
        userEmail: account?.email,
        account: account
    };
};

export default connect(mapStateToProps)(GroupInviteAuth); 